import { isNil } from 'lodash';
import { useRouter } from 'next/router';
import NProgress from 'nprogress';
import { useRef } from 'react';
import * as React from 'react';
import {
  Modal,
  ModalBody,
  ModalFooterDefault,
  ModalHeaderDefault,
} from 'ui-components/dist/Modal';
import { isSSR } from '~/utils-ts';

const msgLeavePage =
  'Your changes have not been saved. Are you sure you want to leave this page?';

interface IInnerComponentProps {
  isEnabled: boolean;
}

const InnerComponent = ({ isEnabled }: IInnerComponentProps) => {
  const router = useRouter();
  const [showModal, setShowModal] = React.useState(false);
  const [newUrl, setNewUrl] = React.useState<string | undefined>();
  const [currentUrl] = React.useState(
    isSSR ? '' : window.location.pathname + window.location.search,
  );

  const isEnabledRef = useRef<boolean>();
  const newUrlRef = useRef<string>();

  // most likely a hack to get ref value straight
  isEnabledRef.current = isEnabled;
  newUrlRef.current = newUrl;

  React.useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (
        isEnabledRef.current &&
        isNil(newUrlRef.current) &&
        window.location.pathname !== url &&
        url !== currentUrl
      ) {
        setNewUrl(url);
        NProgress.done();
        setShowModal(true);

        // eslint-disable-next-line no-throw-literal
        throw Error('KRI-IGNORE: Prevent Navigate Error');
      } else {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        router.events.off('routeChangeStart', handleRouteChange);
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isEnabledRef.current) {
        event.returnValue = msgLeavePage;
        return msgLeavePage;
      }
    };

    // Path changes
    router.events.on('routeChangeStart', handleRouteChange);

    //Refresh browser or close tab
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Back button of the browser
    router.beforePopState(({ as }) => {
      if (
        isEnabledRef.current &&
        isNil(newUrlRef.current) &&
        window.location.pathname !== currentUrl
      ) {
        setNewUrl(as);
        NProgress.done();
        setShowModal(true);
        return false;
      }

      return true;
    });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router, currentUrl]);

  const handleOnCancel = () => {
    //Restore previous state after next/router popstate when user click the back button of the browser
    if (window.location.pathname !== currentUrl) {
      window.history.pushState({}, '', newUrl);
    }

    setNewUrl(undefined);
    setShowModal(false);

    //Restore current state after next/router popstate when user click the back button of the browser
    router.push(currentUrl, undefined, { shallow: true });
  };

  const handleonConfirm = React.useCallback(() => {
    if (newUrl) {
      setShowModal(false);
      router.push(newUrl);
    }
  }, [newUrl, router]);

  return showModal ? (
    <Modal
      closeOnOverlayClick={false}
      isOpen={showModal}
      onClose={handleOnCancel}
    >
      <ModalHeaderDefault title="Leave page?" />
      <ModalBody>{msgLeavePage}</ModalBody>
      <ModalFooterDefault
        secondary={{
          onClick: handleOnCancel,
          title: 'Stay',
        }}
        primary={{
          onClick: handleonConfirm,
          title: 'Yes, leave',
        }}
      />
    </Modal>
  ) : null;
};

export default InnerComponent;
